import { z } from 'zod';

const PROBE_ORIGIN = 'https://probe.invalid';

/**
 * Pathname of a same-origin redirect target, or null.
 *
 * Resolved, not prefix-matched: browsers strip ASCII tab, LF and CR before parsing, so
 * `/<TAB>\host` reaches the same host as `//host` while passing any prefix check.
 */
export function relativePathname(value: string): string | null {
  if (!value.startsWith('/')) {
    return null;
  }
  try {
    const url = new URL(value, PROBE_ORIGIN);
    return url.origin === PROBE_ORIGIN ? url.pathname : null;
  } catch {
    return null;
  }
}

/** Same-origin guard for a redirect target. */
export function isRelativePath(value: string): boolean {
  return relativePathname(value) !== null;
}

/** Search param carrying a post-navigation target. */
export const RETURN_TO_PARAM = 'to';

/** The `?to=` target from a raw query string, or null when absent or not same-origin. */
export function returnToFromSearch(searchStr: string): string | null {
  const to = new URLSearchParams(searchStr).get(RETURN_TO_PARAM);
  return to !== null && isRelativePath(to) ? to : null;
}

export const relativePathSchema = z.string().refine(isRelativePath, 'Must be a relative path');

/**
 * Search params every declared schema accepts, merged in by `definePath`, so a param promoted out of
 * a `?context=` envelope (see `./searchContext`) is typed and retained on any route rather than
 * dropped by a stricter schema. A path declaring no schema keeps TanStack's unvalidated passthrough,
 * which carries them just as well.
 *
 * Here rather than in `./searchContext`: `definePath` sits in the build-time module graph (the Vite
 * config loads it for path translation), and this module is the one already there.
 */
export const baseSearchSchema = z.object({
  [RETURN_TO_PARAM]: relativePathSchema.optional().catch(undefined),
});
